import { bisector } from "d3-array";
import { scaleTime, ScaleTime } from "d3-scale";
import { ChartData } from "../interfaces/chart-data";
import { LineChartScaleSpecifics } from "./line-chart-scale-specifics";
import {
    LineChartXDomain,
    LineChartTooltipFromatterInputData,
} from "./line-chart";

export class LineChartTimeScaleSpecifics
    implements LineChartScaleSpecifics<ScaleTime<number, number>> {
    public xScale(data: ChartData, width: number): ScaleTime<number, number> {
        const dates = this.xAxisEntries(data);
        return scaleTime()
            .domain([dates[0], dates[dates.length - 1]])
            .range([0, width])
            .nice();
    }

    xAxisEntries(data: ChartData): Date[] {
        return Object.keys(data).map((x) => new Date(x));
    }

    mapValues(
        groupName: string,
        data: ChartData
    ): (k: string) => { key: Date; value: number } {
        return (key) => ({ key: new Date(key), value: +data[key][groupName] });
    }

    /** Check if the max label width is too wide.
     *  For time scales we have to check against the distance of consecutive ticks because there is not bandwidth() */
    isXLabelTooWide(
        xScale: ScaleTime<number, number>,
        maxXTickLabelWidth: number,
        xAxisTicks: LineChartXDomain[]
    ) {
        const timeScale = xScale as ScaleTime<number, number>;
        const { rotate } = xAxisTicks
            .map((d) => new Date(d))
            .reduce(
                (agg, d) => {
                    return {
                        prev: d,
                        rotate:
                            maxXTickLabelWidth >
                            timeScale(d) - timeScale(agg.prev),
                    };
                },
                { prev: new Date(0), rotate: false }
            );
        return rotate;
    }

    closestDataPointFnWith(
        data: ChartData,
        xScale: ScaleTime<number, number>,
        offsetX: number,
        selectedGroup: string
    ): (mx: number) => LineChartTooltipFromatterInputData {
        const bisectFn = bisector((d: any) => d.key).left;

        return (mx) => {
            const dataArray = Object.entries(data).map((r) => ({
                key: new Date(r[0]),
                values: r[1],
            }));
            const key = xScale.invert(mx - offsetX);
            const index = bisectFn(dataArray, key, 1);
            const a = dataArray[index - 1];
            const b = dataArray[index];
            const closestDataPoint =
                b &&
                key.getTime() - a.key.getTime() >
                    b.key.getTime() - key.getTime()
                    ? b
                    : a;
            const values =
                selectedGroup && selectedGroup in closestDataPoint.values
                    ? {
                          [selectedGroup]:
                              closestDataPoint.values[selectedGroup],
                      }
                    : closestDataPoint.values;
            return {
                key: closestDataPoint.key,
                values,
            };
        };
    }

    xOffset(): number {
        return 0;
    }

    xPositionForKey(xScale: ScaleTime<number, number>) {
        return (key: Date) => xScale(key);
    }
}
